"use client"
import { create } from "zustand"

interface ActivitySharedDataState {
  activities: any[],
  activity: any,
  activitiesByState: any[],
  error: string | null,
  loading: boolean,

  fetchActivities: () => Promise<void>,
  fetchActivity: (slug: string) => Promise<void>,
  fetchActivitiesByState: (slug: string) => Promise<void>,
  fetchActivitiesFull: (slug: string) => Promise<void>,
}

export const useActivitySharedData = create<ActivitySharedDataState>((set) => ({
  activities: [],
  activity: null,
  activitiesByState: [],
  error: null,
  loading: false,

  //------------------------------------
  // 📌 Fetch ALL activities
  //------------------------------------
  fetchActivities: async () => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}allactivity`)
      if (!res.ok) throw new Error("Failed to fetch activities")

      const data = await res.json()
      set({ activities: data.activety.data })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // 📌 Fetch ONE activity by slug
  //------------------------------------
  fetchActivity: async (slug: string) => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}activity/${slug}`)
      if (!res.ok) throw new Error("Failed to fetch activity")

      const data = await res.json()
      set({ activity: data.activety })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // 📌 Fetch Activities by State
  //------------------------------------
  fetchActivitiesByState: async (slug: string) => {
    set({ loading: true, error: null })
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}activitiesbystate/${slug}`
      )
      if (!res.ok) throw new Error("Failed to fetch activities by state")

      const data = await res.json()
      set({ activitiesByState: data.activities.data })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },

  //------------------------------------
  // ⭐ Fetch ACTIVITY + ACTIVITIES BY STATE (FULL DATA)
  //------------------------------------
  fetchActivitiesFull: async (slug: string) => {
    set({ loading: true, error: null })

    try {
      const [activityRes, activitiesByStateRes] = await Promise.all([
        fetch(`${process.env.NEXT_PUBLIC_API_URL}allactivity/${slug}`),
        fetch(`${process.env.NEXT_PUBLIC_API_URL}activitiesbystate/${slug}`),
      ])

      if (!activityRes.ok) throw new Error("Failed to fetch activity")
      if (!activitiesByStateRes.ok) throw new Error("Failed to fetch activities by state")

      const activityData = await activityRes.json()
      const activitiesStateData = await activitiesByStateRes.json()

      set({
        activity: activityData.activity,
        activitiesByState: activitiesStateData.activities,
      })
    } catch (err: any) {
      set({ error: err.message })
    } finally {
      set({ loading: false })
    }
  },
}))
